// ─── Show-name color derivation ─────────────────────────────────────
// Deterministic HSL palette for the "show tag" pills sprinkled through
// the guide, browse, and detail pages. Same show → same color, always.
// We keep saturation + lightness fixed so every pill sits inside the
// retro CRT palette (no eye-searing neon), and vary only the hue via a
// tiny djb2-style string hash on the show title.
//
// Kept lean on purpose — this is a display-only helper, not a design
// system. A tape without a show_title should render nothing (return
// null values) so callers can conditionally skip the pill entirely.

function hashHue(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) + s.charCodeAt(i);
    h |= 0; // int32
  }
  return Math.abs(h) % 360;
}

/**
 * Returns { color, border, bg } CSS strings for a given show title, or
 * null when the title is empty. Colors use CSS `hsl(h s% l%)` so they
 * blend with the existing phosphor palette (var(--phosphor) is
 * ~hsl(120 100% 40%) — our tags sit in the muted-mid-lightness band).
 */
export function showColor(showTitle) {
  const s = (showTitle || "").trim();
  if (!s) return null;
  const hue = hashHue(s.toLowerCase());
  return {
    color: `hsl(${hue}, 70%, 62%)`,
    border: `hsl(${hue}, 65%, 45%)`,
    bg: `hsla(${hue}, 70%, 50%, 0.12)`,
  };
}

/**
 * Compact display label for a show title — long TMDb titles like
 * "Adventures of Sonic the Hedgehog" get compressed to fit the tight
 * pills. Preserves the "meaningful" head of the title while stripping
 * generic connectives.
 */
export function shortShowLabel(showTitle, max = 22) {
  const s = (showTitle || "").trim();
  if (!s) return "";
  if (s.length <= max) return s;
  // Drop very generic joiners so "Adventures of Sonic the Hedgehog"
  // becomes "Adventures Sonic Hedgehog" — usually fits under 22 chars.
  const dropped = s.replace(/\b(the|of|and|a|an)\b/gi, "").replace(/\s+/g, " ").trim();
  if (dropped.length <= max) return dropped;
  return dropped.slice(0, max - 1).trimEnd() + "…";
}
